import PropTypes from 'prop-types';

import { useInView } from 'react-intersection-observer';

import { connect } from 'react-redux';
import { useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { MetaData } from 'Components/MetaData';
import { Typography } from 'Components/Typography';
import ProductsGrid from '../Components/ProductsGrid';

import currencies from 'constants/currencies';

import BlockLayout from 'Blocks/BlockLayout';
import HeroWithBackground from 'Blocks/HeroWithBackground';
import { ProductsFeatured } from 'Blocks/ProductsFeatured/ProductsFeatured';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';
import LoadingScene from 'Scenes/LoadingScene';

import { hairCareCollection } from 'assets/content/productPages/haircareCollection';

import { shouldShowScalpSerumContent } from 'dux/featureFlags/selectors';
import * as productsSelectors from 'dux/products/selectors';
import { getUserCurrency } from 'dux/user/selectors';
import { useFetchProductsList } from 'dux/products/hooks';

const ProductCollectiondWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroWrapper = styled.div`
  width: 100%;

  section {
    div {
      margin-top: 0;
      padding-top: 48px;
    }
  }

  ${legacyTheme.breakpoints.up('md')} {
    section {
      div {
        margin-top: 144px;
        padding-top: 0;
      }
    }

    h1 {
      font-size: 57px;
    }
  }
`;

const ProductsFeaturedWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  padding: 45px ${legacyTheme.spacing.s24};
  gap: ${legacyTheme.spacing.s24};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 45px 70px;
    flex-direction: row;
    gap: ${legacyTheme.spacing.s24};
  }

  ${legacyTheme.breakpoints.up('lg')} {
    width: 90%;
    height: 327px;
    margin: 90px 0;
    padding: 45px 15px;
    gap: ${legacyTheme.spacing.s36};
  }
`;

const HaircareProductCollection = ({ content, userCurrency }) => {
  const [refProductFeatured, inViewProductFeatured] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const productsBlockContent = useAppSelector(productsSelectors.getHaircareProductsBlockContent);
  const showScalpSerumContent = useAppSelector(shouldShowScalpSerumContent);
  const { isLoaded } = useFetchProductsList();

  if (!isLoaded) {
    return <LoadingScene />;
  }

  const { skincare, accessories } = content.featured;
  const underButtonTextLabel =
    userCurrency === currencies.CAD ? 'Not currently available in Canada' : null;

  return (
    <>
      <MetaData
        description="Get custom hair products and supplements formulated to meet all of your specific unique hair needs and goals. Take your consultation and we’ll create a hair routine and formulas that’s just for you!"
        title="Custom Hair Products & Supplements – Prose | Haircare"
      />

      <BaseScene footer header>
        <HeroWrapper>
          <HeroWithBackground
            colorButton={showScalpSerumContent ? theme.colors.primary[400] : undefined}
            content={
              showScalpSerumContent
                ? {
                    ...content.hero,
                    imageDesktop: content.hero.imageDesktopWithScalpSerum,
                    imageMobile: content.hero.imageMobileWithScalpSerum,
                    color: 'white',
                  }
                : content.hero
            }
          />
        </HeroWrapper>

        <ProductCollectiondWrapper>
          <ProductsGrid background="white" content={productsBlockContent} justifyContent="center" />
        </ProductCollectiondWrapper>

        <ProductsFeaturedWrapper ref={refProductFeatured}>
          {inViewProductFeatured && (
            <>
              <ProductsFeatured
                ctaLabel={skincare.ctaLabel}
                dataClick={skincare.dataClick}
                dataFrom={skincare.dataFrom}
                image={{
                  alt: skincare.imgAlt,
                  src: {
                    mobile: skincare.imgMobile,
                    tablet: skincare.imgDesktop,
                    desktop: skincare.imgDesktop,
                  },
                }}
                title={skincare.title}
                underButtonTextLabel={underButtonTextLabel}
                url={skincare.url}
              />

              <ProductsFeatured
                ctaLabel={accessories.ctaLabel}
                image={{
                  alt: accessories.imgAlt,
                  src: {
                    mobile: accessories.imgMobile,
                    tablet: accessories.imgDesktop,
                    desktop: accessories.imgDesktop,
                  },
                }}
                title={accessories.title}
                underButtonTextLabel={underButtonTextLabel}
                url={accessories.url}
              />
            </>
          )}
        </ProductsFeaturedWrapper>

        <BlockLayout backgroundColor="darkerBeige" color="noir" contentAlign="center">
          <Typography markupName="h1" paragraph variant="h2">
            {content.seoBlurb.headline}
          </Typography>

          <Typography color="grey" variant="p1">
            {content.seoBlurb.subHead}
          </Typography>
        </BlockLayout>
      </BaseScene>
    </>
  );
};

HaircareProductCollection.propTypes = {
  content: PropTypes.shape({
    hero: PropTypes.shape({
      color: PropTypes.string,
      headline: PropTypes.node,
      imageDesktop: srcOrNextImageProp,
      imageMobile: srcOrNextImageProp,
      imageDesktopWithScalpSerum: srcOrNextImageProp,
      imageMobileWithScalpSerum: srcOrNextImageProp,
    }),
    featured: PropTypes.shape({
      skincare: PropTypes.shape({
        ctaLabel: PropTypes.string,
        imgAlt: PropTypes.string,
        imgMobile: srcOrNextImageProp,
        imgDesktop: srcOrNextImageProp,
        title: PropTypes.string,
        url: PropTypes.string,
        dataClick: PropTypes.string,
        dataFrom: PropTypes.string,
      }),
      supplements: PropTypes.shape({
        ctaLabel: PropTypes.string,
        imgMobile: srcOrNextImageProp,
        imgDesktop: srcOrNextImageProp,
        title: PropTypes.string,
        url: PropTypes.string,
      }),
      accessories: PropTypes.shape({
        ctaLabel: PropTypes.string,
        imgAlt: PropTypes.string,
        imgMobile: PropTypes.oneOfType([
          PropTypes.shape({
            src: PropTypes.string.isRequired,
          }),
          PropTypes.func,
          PropTypes.string,
        ]),
        imgDesktop: PropTypes.oneOfType([
          PropTypes.shape({
            src: PropTypes.string.isRequired,
          }),
          PropTypes.func,
          PropTypes.string,
        ]),
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    }),
    seoBlurb: PropTypes.shape({
      headline: PropTypes.string,
      subHead: PropTypes.string,
    }),
  }),
  userCurrency: PropTypes.string.isRequired,
};

HaircareProductCollection.defaultProps = {
  content: hairCareCollection,
};

export default connect(state => ({
  userCurrency: getUserCurrency(state),
}))(HaircareProductCollection);
